import {
  canTransition,
  formatFull,
  isDomesticCanadian,
  type Appointment,
  type JoinResponse,
  type Principal,
} from "@so/shared";
import { lazy, Suspense, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  useAcceptAppointment,
  useAppointment,
  useAvailability,
  useCancelAppointment,
  useEndMeeting,
  useJoinMeeting,
  useMe,
  useOutcome,
  useReinstateAppointment,
  useRescheduleAppointment,
  type Me,
} from "../api/queries.js";
import { AppointmentTime, ResidencyNote, SpecialtyBadge, StatusBadge } from "../components/appointment.js";
import { Documents } from "../components/Documents.js";
import { OpinionForm } from "../components/OpinionForm.js";
import { OpinionPending, OpinionView } from "../components/OpinionView.js";
import {
  Alert,
  Button,
  Card,
  ErrorNote,
  Field,
  Loading,
  PageHeader,
  Select,
  Textarea,
} from "../components/ui.js";
/**
 * The Chime SDK is around a megabyte and is only needed once a consultation is
 * actually joined. Loading it eagerly would put it in the initial bundle of an
 * embeddable widget that mostly renders lists.
 */
const VideoCall = lazy(() =>
  import("../components/VideoCall.js").then((m) => ({ default: m.VideoCall })),
);

/** The domain rules need a Principal; `me` is the same person in wire shape. */
function toPrincipal(me: Me): Principal {
  return {
    userId: me.userId,
    email: me.email,
    name: me.name,
    roles: me.roles,
    ...(me.timezone === null ? {} : { timezone: me.timezone }),
  };
}

const EARLY_JOIN_MS = 15 * 60_000;
const LATE_JOIN_MS = 60 * 60_000;

function RescheduleBox({
  appointment,
  mode,
  onDone,
}: {
  readonly appointment: Appointment;
  readonly mode: "reschedule" | "reinstate";
  readonly onDone: () => void;
}) {
  const { data: me } = useMe();
  const { data: availability } = useAvailability(appointment.clinician_id);
  const reschedule = useRescheduleAppointment();
  const reinstate = useReinstateAppointment();

  const [startsAt, setStartsAt] = useState("");
  const viewerTimezone = me?.timezone ?? "UTC";

  const mutation = mode === "reinstate" ? reinstate : reschedule;
  const slots = availability?.slots ?? [];

  async function submit() {
    if (startsAt === "") return;
    await mutation.mutateAsync({ id: appointment.appointment_id, body: { starts_at: startsAt } });
    onDone();
  }

  return (
    <Card className="p-5">
      <h3 className="mb-1 text-[16px] font-semibold">
        {mode === "reinstate" ? "Rebook this consultation" : "Move this consultation"}
      </h3>
      <p className="text-ink-soft mb-4 text-[14px] leading-relaxed">
        {mode === "reinstate"
          ? "Choose a new time you have already agreed with both the patient and the specialist. Both will be emailed."
          : "The specialist will need to confirm the new time unless you are coordinating on their behalf."}
      </p>

      {slots.length === 0 ? (
        <Alert tone="caution">
          This specialist has no open windows. Ask them to publish more availability first.
        </Alert>
      ) : (
        <div className="space-y-4">
          <Field label="New time">
            <Select value={startsAt} onChange={(e) => setStartsAt(e.target.value)}>
              <option value="">Choose a time…</option>
              {slots.map((slot) => (
                <option key={slot.starts_at} value={slot.starts_at}>
                  {formatFull(slot.starts_at, viewerTimezone)}
                </option>
              ))}
            </Select>
          </Field>

          {mutation.error !== null && <ErrorNote error={mutation.error} />}

          <div className="flex gap-2">
            <Button loading={mutation.isPending} disabled={startsAt === ""} onClick={() => void submit()}>
              Confirm new time
            </Button>
            <Button variant="ghost" onClick={onDone}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function CancelBox({
  appointment,
  onDone,
}: {
  readonly appointment: Appointment;
  readonly onDone: () => void;
}) {
  const cancel = useCancelAppointment();
  const [reason, setReason] = useState("");

  return (
    <Card className="p-5">
      <h3 className="mb-1 text-[16px] font-semibold">Cancel this consultation</h3>
      <p className="text-ink-soft mb-4 text-[14px] leading-relaxed">
        The other participant will be emailed, and the time will go back into the specialist&apos;s
        calendar.
      </p>

      <div className="space-y-4">
        <Field label="Reason" hint="Shared with the coordination team, not with the patient.">
          <Textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            placeholder="Called into emergency theatre."
          />
        </Field>

        {cancel.error !== null && <ErrorNote error={cancel.error} />}

        <div className="flex gap-2">
          <Button
            variant="danger"
            loading={cancel.isPending}
            onClick={() =>
              void cancel
                .mutateAsync({
                  id: appointment.appointment_id,
                  body: reason.trim() === "" ? {} : { reason: reason.trim() },
                })
                .then(onDone)
            }
          >
            Cancel consultation
          </Button>
          <Button variant="ghost" onClick={onDone}>
            Keep it
          </Button>
        </div>
      </div>
    </Card>
  );
}

export function ConsultationDetail() {
  const { id = "" } = useParams();
  const { data: me } = useMe();
  const { data: appointment, isPending, error } = useAppointment(id);

  const accept = useAcceptAppointment();
  const join = useJoinMeeting(id);
  const endMeeting = useEndMeeting(id);

  const [panel, setPanel] = useState<"none" | "cancel" | "reschedule" | "reinstate">("none");
  const [call, setCall] = useState<JoinResponse | undefined>();

  const { data: report } = useOutcome(id, appointment?.status === "reported");

  if (isPending) return <Loading label="Loading consultation" />;
  if (error) return <ErrorNote error={error} />;
  if (!appointment || !me) return <ErrorNote error={new Error("Consultation not found.")} />;

  const principal = toPrincipal(me);
  const viewerTimezone = me.timezone ?? "UTC";

  const isPatient = appointment.patient_id === me.userId;
  const isClinician = appointment.clinician_id === me.userId;
  const isCoordinator = me.roles.includes("admin");

  // The same rules the server enforces, so the UI never offers an action that
  // would come back refused.
  const mayAccept = canTransition("accept", appointment, principal).ok && (isClinician || isCoordinator);
  const mayCancel = canTransition("cancel", appointment, principal).ok;
  const mayReinstate = canTransition("reinstate", appointment, principal).ok;
  const mayComplete = canTransition("complete", appointment, principal).ok && (isClinician || isCoordinator);
  const mayReport = canTransition("report", appointment, principal).ok && (isClinician || isCoordinator);

  const now = Date.now();
  const opensAt = Date.parse(appointment.starts_at) - EARLY_JOIN_MS;
  const closesAt = Date.parse(appointment.ends_at) + LATE_JOIN_MS;
  const roomOpen = appointment.status === "confirmed" && now >= opensAt && now <= closesAt;

  const counterpart = isPatient ? appointment.clinician_name : appointment.patient_name;

  if (call !== undefined) {
    return (
      <Suspense fallback={<Loading label="Starting the consultation" />}>
        <VideoCall
          join={call}
          canEnd={mayComplete}
          onLeave={() => setCall(undefined)}
          onEnd={async () => {
            await endMeeting.mutateAsync();
            setCall(undefined);
          }}
        />
      </Suspense>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow={isPatient ? "Your consultation" : "Consultation"}
        title={counterpart}
        actions={
          <Link to="/consultations">
            <Button variant="ghost">Back</Button>
          </Link>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-6">
          <Card className="p-5">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <StatusBadge status={appointment.status} />
              <SpecialtyBadge specialty={appointment.specialty} />
            </div>

            <AppointmentTime
              appointment={appointment}
              viewerTimezone={viewerTimezone}
              showCounterpart
            />

            <div className="border-line mt-4 border-t pt-4">
              <h3 className="text-ink-faint mb-1.5 font-mono text-[11.5px] font-medium tracking-[0.08em] uppercase">
                Reason for the referral
              </h3>
              <p className="text-[15px] leading-relaxed whitespace-pre-line">
                {appointment.reason}
              </p>
            </div>

            {appointment.cancelled_reason !== undefined && (
              <div className="mt-4">
                <Alert tone="warn" title="Cancelled">
                  {appointment.cancelled_reason}
                </Alert>
              </div>
            )}

            <div className="border-line mt-4 border-t pt-3">
              <ResidencyNote region={appointment.media_region} />
            </div>
          </Card>

          {appointment.status === "reported" && report !== undefined && (
            <OpinionView report={report} viewerTimezone={viewerTimezone} />
          )}

          {appointment.status === "reported" && report === undefined && (
            <Loading label="Loading the opinion" />
          )}

          {appointment.status === "completed" && mayReport && <OpinionForm appointmentId={id} />}

          {appointment.status === "completed" && !mayReport && (
            <OpinionPending clinicianName={appointment.clinician_name} />
          )}

          <Documents
            appointmentId={id}
            canUpload={(isPatient || isCoordinator) && appointment.status !== "reported" && appointment.status !== "cancelled"}
            readOnlyReason={
              appointment.status === "reported"
                ? "Records are locked once the second opinion has been issued, so the opinion always reflects what was reviewed."
                : undefined
            }
          />
        </div>

        <div className="space-y-4">
          {roomOpen && (
            <Card className="p-5">
              <h3 className="mb-1 text-[16px] font-semibold">The consultation room is open</h3>
              <p className="text-ink-soft mb-4 text-[14px] leading-relaxed">
                {isDomesticCanadian(appointment.media_region)
                  ? "Video and audio stay within Canada."
                  : "Video is hosted in the region nearest the patient."}
              </p>

              {join.error !== null && (
                <div className="mb-3">
                  <ErrorNote error={join.error} />
                </div>
              )}

              <Button
                full
                loading={join.isPending}
                onClick={() => void join.mutateAsync().then(setCall)}
              >
                Join the consultation
              </Button>
            </Card>
          )}

          {appointment.status === "confirmed" && !roomOpen && (
            <Card className="p-5">
              <h3 className="mb-1 text-[16px] font-semibold">Confirmed</h3>
              <p className="text-ink-soft text-[14px] leading-relaxed">
                {now < opensAt
                  ? "The room opens fifteen minutes before the scheduled time. You will be able to join from here."
                  : "This consultation window has passed."}
              </p>
            </Card>
          )}

          {panel === "none" && (
            <Card className="p-5">
              <h3 className="mb-3 text-[16px] font-semibold">Actions</h3>
              <div className="space-y-2">
                {mayAccept && (
                  <Button
                    full
                    loading={accept.isPending}
                    onClick={() => void accept.mutateAsync({ id })}
                  >
                    Accept this consultation
                  </Button>
                )}

                {mayReinstate && (
                  <Button full onClick={() => setPanel("reinstate")}>
                    Rebook at a new time
                  </Button>
                )}

                {appointment.status === "requested" || appointment.status === "confirmed" ? (
                  <Button variant="secondary" full onClick={() => setPanel("reschedule")}>
                    Move to another time
                  </Button>
                ) : null}

                {mayCancel && (
                  <Button variant="secondary" full onClick={() => setPanel("cancel")}>
                    Cancel consultation
                  </Button>
                )}

                {!mayAccept && !mayCancel && !mayReinstate && appointment.status === "reported" && (
                  <p className="text-ink-faint text-[14px] leading-relaxed">
                    This consultation is complete and the second opinion has been issued.
                  </p>
                )}
              </div>

              {accept.error !== null && (
                <div className="mt-3">
                  <ErrorNote error={accept.error} />
                </div>
              )}
            </Card>
          )}

          {panel === "cancel" && (
            <CancelBox appointment={appointment} onDone={() => setPanel("none")} />
          )}

          {(panel === "reschedule" || panel === "reinstate") && (
            <RescheduleBox
              appointment={appointment}
              mode={panel}
              onDone={() => setPanel("none")}
            />
          )}

          {isCoordinator && !isPatient && !isClinician && (
            <Alert tone="caution">
              You are viewing this as a care coordinator. Everything you do here is recorded
              against your name in the access log.
            </Alert>
          )}
        </div>
      </div>
    </>
  );
}
